import { useApolloClient } from '@apollo/client';
import { Form, message } from 'antd';
import { Formik } from 'formik';
import { pick } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type InsuranceModuleSpecsFragment } from '../../../../api/fragments/InsuranceModuleSpecs';
import {
    type UpdateInsuranceModuleMutation,
    UpdateInsuranceModuleDocument,
    type UpdateInsuranceModuleMutationVariables,
} from '../../../../api/mutations/updateInsuranceModule';
import { type InsuranceModuleSettings } from '../../../../api/types';
import InputField from '../../../../components/fields/InputField';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';

export type InsuraneModuleUpdateFormProps = {
    module: InsuranceModuleSpecsFragment;
    onComplete: () => void;
};

type FormValues = InsuranceModuleSettings;

const InsuraneModuleUpdateForm = ({ module, onComplete }: InsuraneModuleUpdateFormProps) => {
    const { t } = useTranslation(['insuranceModuleDetails']);
    const apolloClient = useApolloClient();

    const moduleId = module.id;

    const onSubmit = useHandleError(
        async (values: FormValues) => {
            message.loading({
                content: t('insuranceModuleDetails:updateDrawer.messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<UpdateInsuranceModuleMutation, UpdateInsuranceModuleMutationVariables>({
                    mutation: UpdateInsuranceModuleDocument,
                    variables: { moduleId, settings: values },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('insuranceModuleDetails:updateDrawer.messages.updateSuccess'),
                key: 'primary',
            });

            onComplete();
        },
        [onComplete, apolloClient, moduleId, t]
    );

    const initialValues = useMemo(
        (): FormValues => ({
            ...pick(['displayName'], module),
        }),
        [module]
    );

    const validate = useValidator(validators.requiredNonEmptyString('displayName'));

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form
                    id="updateInsuranceModule"
                    layout="vertical"
                    name="updateInsuranceModule"
                    onSubmitCapture={handleSubmit}
                >
                    <InputField
                        {...t('insuranceModuleDetails:updateDrawer.fields.displayName', {
                            returnObjects: true,
                        })}
                        name="displayName"
                        required
                    />
                </Form>
            )}
        </Formik>
    );
};

export default InsuraneModuleUpdateForm;
